const PROFILE_IMAGE_POINTS = 30;
const SHORT_DESCRIPTION_POINTS = 30;
const SIGNATURE_HIGHLIGHTS_POINTS = 25;
const VALUE_PROPS_POINTS = 10;
const HEADLINE_POINTS = 5;
export const STORE_PROFILE_READINESS_KEY_PREFIX = 'storeProfileReadiness:';

const HIGH_THRESHOLD = 75;
const MEDIUM_THRESHOLD = 40;

const normalizeString = (value) => String(value || '').trim();

const parseJsonObject = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const toCleanList = (value) => (
  Array.isArray(value)
    ? value.map((item) => normalizeString(item)).filter(Boolean)
    : []
);

export const getCustomerProfile = (store = {}) => {
  const meta = parseJsonObject(store.storeMetaData);
  const customerProfile = parseJsonObject(meta.customerProfile);
  return {
    headline: normalizeString(customerProfile.headline),
    shortDescription: normalizeString(customerProfile.shortDescription),
    signatureHighlights: toCleanList(customerProfile.signatureHighlights),
    valueProps: toCleanList(customerProfile.valueProps),
    gallery: toCleanList(customerProfile.gallery)
  };
};

export const computeStoreProfileReadiness = (store = {}) => {
  const customerProfile = getCustomerProfile(store);
  const hasProfileImage = Boolean(store.imgUploaded || normalizeString(store.profileImageUrl));
  const hasShortDescription = Boolean(customerProfile.shortDescription);
  const hasRequiredHighlights = customerProfile.signatureHighlights.length >= 2;
  const hasValueProp = customerProfile.valueProps.length >= 1;
  const hasHeadline = Boolean(customerProfile.headline);

  let completionScore = 0;
  if (hasProfileImage) completionScore += PROFILE_IMAGE_POINTS;
  if (hasShortDescription) completionScore += SHORT_DESCRIPTION_POINTS;
  if (hasRequiredHighlights) completionScore += SIGNATURE_HIGHLIGHTS_POINTS;
  if (hasValueProp) completionScore += VALUE_PROPS_POINTS;
  if (hasHeadline) completionScore += HEADLINE_POINTS;

  const missingItems = [];
  if (!hasShortDescription) missingItems.push('Add a short description');
  if (!hasProfileImage) missingItems.push('Add a profile image');
  if (!hasRequiredHighlights) missingItems.push('Add 2 signature highlights');
  if (!hasValueProp) missingItems.push('Add 1 value point customers care about');
  if (!hasHeadline) missingItems.push('Add a short headline');

  const completionLevel = completionScore >= HIGH_THRESHOLD
    ? 'HIGH'
    : completionScore >= MEDIUM_THRESHOLD
      ? 'MEDIUM'
      : 'LOW';

  return {
    completionScore,
    completionLevel,
    missingItems,
    customerProfile
  };
};

export const persistStoreProfileReadiness = (storeUuid, readiness) => {
  if (!storeUuid || typeof window === 'undefined' || !readiness) {
    return;
  }
  window.localStorage.setItem(
    `${STORE_PROFILE_READINESS_KEY_PREFIX}${storeUuid}`,
    JSON.stringify({
      completionLevel: readiness.completionLevel,
      completionScore: readiness.completionScore
    })
  );
  try {
    window.dispatchEvent(new CustomEvent('storeProfileReadinessUpdated', { detail: { storeUuid } }));
  } catch {
    // no-op
  }
};

export const readStoreProfileReadiness = (storeUuid) => {
  if (!storeUuid || typeof window === 'undefined') {
    return null;
  }
  const raw = window.localStorage.getItem(`${STORE_PROFILE_READINESS_KEY_PREFIX}${storeUuid}`);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      completionLevel: String(parsed.completionLevel || '').toUpperCase(),
      completionScore: Number(parsed.completionScore || 0)
    };
  } catch {
    return null;
  }
};
